// /src/components/tools/memberProfileCard/PerkThemeLightningCanvas.jsx
// Procedural lightning via recursive midpoint displacement + sky flash.
// quantity (0–100): strike frequency. height (0–100): how far bolts reach from the top.

import React, { useEffect, useRef } from 'react';
import { lightningHeightClass } from '../../../utils/memberProfileThemeDisplay';

const CORE = 'rgba(255, 255, 255, 0.95)';
const GLOW = 'rgba(90, 195, 255, 0.55)';
const GLOW_SOFT = 'rgba(90, 195, 255, 0.22)';

function rand(min, max) {
  return min + Math.random() * (max - min);
}

function buildBolt(start, end, {
  generations = 6,
  displace = 0,
  branchChance = 0.32,
  thickness = 2.4,
} = {}) {
  const segments = [];

  function split(a, b, gen, disp, w, canBranch) {
    if (gen <= 0) {
      segments.push({ a, b, w });
      return;
    }

    const mx = (a.x + b.x) * 0.5 + rand(-disp, disp);
    const my = (a.y + b.y) * 0.5 + rand(-disp * 0.35, disp * 0.35);
    const mid = { x: mx, y: my };

    split(a, mid, gen - 1, disp * 0.5, w, canBranch);
    split(mid, b, gen - 1, disp * 0.5, w, canBranch);

    if (canBranch && gen >= 3 && Math.random() < branchChance) {
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const side = Math.random() < 0.5 ? -1 : 1;
      const tip = {
        x: mid.x + dx * rand(0.25, 0.55) + side * rand(disp * 0.4, disp * 1.1),
        y: mid.y + dy * rand(0.35, 0.7),
      };
      split(mid, tip, gen - 2, disp * 0.45, w * 0.45, false);
    }
  }

  const initialDisplace = displace || Math.hypot(end.x - start.x, end.y - start.y) * 0.28;
  split(start, end, generations, initialDisplace, thickness, true);
  return segments;
}

function drawSegments(ctx, segments, {
  color,
  widthScale = 1,
  blur = 0,
  alpha = 1,
}) {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  if (blur > 0) {
    ctx.shadowColor = color;
    ctx.shadowBlur = blur;
  }
  for (const seg of segments) {
    ctx.beginPath();
    ctx.lineWidth = Math.max(0.4, seg.w * widthScale);
    ctx.moveTo(seg.a.x, seg.a.y);
    ctx.lineTo(seg.b.x, seg.b.y);
    ctx.stroke();
  }
  ctx.restore();
}

function drawSkyFlash(ctx, w, h, intensity, originX) {
  const g = ctx.createRadialGradient(originX, 0, 0, originX, 0, w * 0.7);
  g.addColorStop(0, `rgba(220, 240, 255, ${0.22 * intensity})`);
  g.addColorStop(0.35, `rgba(120, 180, 255, ${0.1 * intensity})`);
  g.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);

  const wash = ctx.createLinearGradient(0, 0, 0, h);
  wash.addColorStop(0, `rgba(200, 230, 255, ${0.12 * intensity})`);
  wash.addColorStop(0.55, `rgba(200, 230, 255, ${0.03 * intensity})`);
  wash.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = wash;
  ctx.fillRect(0, 0, w, h);
}

function delayForQuantity(quantity) {
  const q = Math.max(0, Math.min(100, Number(quantity) || 50));
  const minDelay = 10000 - q * 85;
  const span = 3000 - q * 15;
  return Math.max(900, minDelay + Math.floor(Math.random() * Math.max(400, span)));
}

/**
 * @param {{ quantity?: number, height?: number }} props
 */
export default function PerkThemeLightningCanvas({
  quantity = 50,
  height = 45,
}) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const quantityRef = useRef(quantity);

  useEffect(() => {
    quantityRef.current = quantity;
  }, [quantity]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return undefined;

    const reduceMotion =
      typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return undefined;

    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    let cancelled = false;
    let rafId = null;
    let timeoutId = null;
    let resizeObserver = null;

    const syncSize = () => {
      const rect = wrap.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.floor(rect.width));
      const h = Math.max(1, Math.floor(rect.height));
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return { w, h };
    };

    const clear = () => {
      const { w, h } = syncSize();
      ctx.clearRect(0, 0, w, h);
    };

    const scheduleNext = () => {
      if (cancelled) return;
      timeoutId = window.setTimeout(runStrike, delayForQuantity(quantityRef.current));
    };

    const runStrike = () => {
      if (cancelled) return;
      const { w, h } = syncSize();
      if (w < 8 || h < 8) {
        scheduleNext();
        return;
      }

      const startX = rand(w * 0.15, w * 0.85);
      const endX = startX + rand(-w * 0.18, w * 0.18);
      const start = { x: startX, y: -4 };
      // Reach near the bottom of the (height-limited) layer
      const end = { x: endX, y: h * rand(0.78, 0.98) };

      const bolts = [
        buildBolt(start, end, {
          generations: 6,
          branchChance: 0.36,
          thickness: rand(0.9, 1.25),
        }),
      ];
      if (Math.random() < 0.4) {
        const s2x = rand(w * 0.12, w * 0.88);
        bolts.push(
          buildBolt(
            { x: s2x, y: -2 },
            { x: s2x + rand(-w * 0.12, w * 0.12), y: h * rand(0.6, 0.92) },
            {
              generations: 5,
              branchChance: 0.28,
              thickness: rand(0.55, 0.85),
            },
          ),
        );
      }
      const segments = bolts.flat();

      const started = performance.now();
      const duration = 280;

      const frame = (now) => {
        if (cancelled) return;
        const t = Math.min(1, (now - started) / duration);
        let flash;
        if (t < 0.12) flash = t / 0.12;
        else if (t < 0.22) flash = 0.25;
        else if (t < 0.4) flash = 1;
        else flash = 1 - (t - 0.4) / 0.6;

        const boltAlpha = t < 0.18
          ? 1
          : (t < 0.28 ? 0.2 : (t < 0.5 ? 0.95 : flash));

        ctx.clearRect(0, 0, w, h);
        drawSkyFlash(ctx, w, h, Math.max(0, flash) * 0.9, startX);
        drawSegments(ctx, segments, {
          color: GLOW_SOFT,
          widthScale: 2.2,
          blur: 8,
          alpha: 0.4 * boltAlpha,
        });
        drawSegments(ctx, segments, {
          color: GLOW,
          widthScale: 1.35,
          blur: 4,
          alpha: 0.7 * boltAlpha,
        });
        drawSegments(ctx, segments, {
          color: CORE,
          widthScale: 0.75,
          blur: 1,
          alpha: boltAlpha,
        });
        drawSegments(ctx, segments, {
          color: 'rgba(255,255,255,0.95)',
          widthScale: 0.28,
          blur: 0,
          alpha: boltAlpha,
        });

        if (t < 1) {
          rafId = window.requestAnimationFrame(frame);
        } else {
          ctx.clearRect(0, 0, w, h);
          scheduleNext();
        }
      };

      rafId = window.requestAnimationFrame(frame);
    };

    clear();
    timeoutId = window.setTimeout(runStrike, 800 + Math.random() * 1200);

    if (typeof ResizeObserver === 'function') {
      resizeObserver = new ResizeObserver(() => {
        clear();
      });
      resizeObserver.observe(wrap);
    }

    return () => {
      cancelled = true;
      if (timeoutId != null) window.clearTimeout(timeoutId);
      if (rafId != null) window.cancelAnimationFrame(rafId);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [height]);

  return (
    <div
      ref={wrapRef}
      className={[
        'perk-theme-overlay__layer',
        'perk-theme-overlay__layer--lightning',
        lightningHeightClass(height),
      ].join(' ')}
      aria-hidden
    >
      <canvas ref={canvasRef} className="perk-theme-overlay__lightning-canvas" />
    </div>
  );
}
